export const AIRBNB_LISTING_PATTERN = /airbnb\.[a-z.]+\/rooms\//i;
export const AIRBNB_ROOM_ID_PATTERN = /airbnb\.[a-z.]+\/rooms\/(\d+)/i;
export const ICAL_PATTERN = /\.ics(\?|$)/i;
export const HOST_ONBOARDING_RESULT_KEY = 'allureHostOnboardingResult';

/**
 * Extract the numeric Airbnb room id from any Airbnb listing URL.
 * Returns null if no id can be parsed.
 */
export const extractAirbnbRoomId = (value = '') => {
  const match = normalizeHostUrl(value).match(AIRBNB_ROOM_ID_PATTERN);
  return match ? match[1] : null;
};

/**
 * Build a canonical https Airbnb listing URL. On iOS/Android with the Airbnb
 * app installed, this Universal Link / App Link will deep-link straight into
 * the app on that listing page. Falls back to the original URL if no id found.
 */
export const buildAirbnbListingUniversalLink = (value = '') => {
  const roomId = extractAirbnbRoomId(value);
  if (roomId) return `https://www.airbnb.com/rooms/${roomId}`;
  const normalized = normalizeHostUrl(value);
  if (!normalized) return '';
  return /^https?:\/\//i.test(normalized) ? normalized : `https://${normalized}`;
};

const previewCache = new Map();

export const normalizeHostUrl = (value = '') => value.trim();

export const isValidAirbnbListingUrl = (value = '') => AIRBNB_LISTING_PATTERN.test(normalizeHostUrl(value));

export const isValidIcalUrl = (value = '') => ICAL_PATTERN.test(normalizeHostUrl(value));

export const getCachedListingPreview = (url = '') => previewCache.get(normalizeHostUrl(url)) || null;

export const setCachedListingPreview = (url = '', preview) => {
  const normalized = normalizeHostUrl(url);
  if (!normalized || !preview) {
    return;
  }
  previewCache.set(normalized, preview);
};

export const saveHostOnboardingResult = (payload) => {
  sessionStorage.setItem(HOST_ONBOARDING_RESULT_KEY, JSON.stringify(payload));
};

export const getHostOnboardingResult = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HOST_ONBOARDING_RESULT_KEY) || 'null');
  } catch {
    return null;
  }
};

export const clearHostOnboardingResult = () => {
  sessionStorage.removeItem(HOST_ONBOARDING_RESULT_KEY);
};

// ---------------------------------------------------------------------------
// Host listing draft (URL + iCal) carried between the listing form page and
// the legal agreement page. We stash these into sessionStorage so the host
// can refresh / navigate back from the agreement screen without losing input.
// ---------------------------------------------------------------------------
export const HOST_LISTING_DRAFT_KEY = 'allureHostListingDraft';

export const saveHostListingDraft = (payload) => {
  try {
    sessionStorage.setItem(HOST_LISTING_DRAFT_KEY, JSON.stringify(payload || {}));
  } catch { /* noop */ }
};

export const loadHostListingDraft = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HOST_LISTING_DRAFT_KEY) || 'null');
  } catch {
    return null;
  }
};

export const clearHostListingDraft = () => {
  try { sessionStorage.removeItem(HOST_LISTING_DRAFT_KEY); } catch { /* noop */ }
};
